import React, { useEffect } from "react";
import { matchPath } from "react-router";
import { Link, withRouter, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Menu, Divider } from "antd";
import { CollapsableSidebar } from "farmx-web-ui";
import { useSelector, useDispatch } from "react-redux";
import { actions, hooks, selectors } from "farmx-redux-core";
import {
  FaHome,
  FaChartLine,
  FaList,
  FaToggleOn,
  FaMapMarkedAlt,
  FaRegCalendarAlt,
  FaClipboardList,
  FaCog,
  FaSyncAlt,
} from "react-icons/fa";
import { GEC_ENTITY_ID, GEC_TEST_ENTITY_ID } from "../../constants";
import { ROUTES } from "./routes";
import "./Sidebar.css";
import EntitySelect from "./EntitySelect";
import { useCheckIsAdminOperations } from "helper/common";

const { selectSelectedBlocks, selectLoginUserInfo } = selectors;

const { setBlocks } = actions;

const { useRanchBlockSelection, useEntities } = hooks;

const { SubMenu } = Menu;

const getMatchedKey = (location) => {
  const matchedKey = Object.values(ROUTES).find((route) =>
    matchPath(location.pathname, route.key)
  );
  if (matchedKey === undefined) return undefined;
  return matchedKey.key;
};

function Sidebar(props) {
  const location = useLocation();
  const { isMobile, onCollapse, collapsed } = props;
  const dispatch = useDispatch();
  const { activeEntityId } = useEntities();
  const { t } = useTranslation();
  const userInfo = useSelector(selectLoginUserInfo).payload;

  const { selectedObjFromState, blockIds } = useRanchBlockSelection();
  const selectedBlocks = useSelector((state) => selectSelectedBlocks(state));
  const isAdminOperation = useCheckIsAdminOperations();
  const isAdmin = (userInfo && userInfo.admin);

  const blocksFromState = selectedBlocks?.payload || [];
  useEffect(() => {
    if (
      blockIds?.length &&
      !blocksFromState?.length &&
      selectedObjFromState?.value
    ) {
      dispatch(setBlocks(blockIds));
    }
  }, [dispatch, blockIds, blocksFromState, selectedObjFromState]);

  function collapseIfMobile() {
    if (isMobile && onCollapse) onCollapse();
    return null;
  }

  return (
    <CollapsableSidebar
      theme="light"
      onCollapse={onCollapse}
      collapsed={collapsed}
    >
      {!isAdminOperation || !isAdmin ? <>
        {!collapsed && (
          <div className="sidebar-select-margin">
            <EntitySelect />
          </div>
        )}
        <Divider style={{ margin: "0 0 6px 0" }} />
        <Menu
          className="nav-menu side-bar"
          theme="light"
          selectedKeys={[getMatchedKey(location)]}
          mode="inline"
          onClick={collapseIfMobile}
        >
          <Menu.Item key={ROUTES.RECOMMENDATION.key}>
            <Link to={ROUTES.RECOMMENDATION.path}>
              <span className="menu-icon">
                <FaHome />
              </span>
              <span className="menu-title">{t("Home")}</span>
            </Link>
          </Menu.Item>
          <Menu.Item key={ROUTES.MAP.key}>
            <Link to={ROUTES.MAP.path}>
              <span className="menu-icon">
                <FaMapMarkedAlt />
              </span>
              <span className="menu-title">{t("Map")}</span>
            </Link>
          </Menu.Item>
          <Menu.Item key={ROUTES.LIST.key}>
            <Link to={ROUTES.LIST.path}>
              <span className="menu-icon">
                <FaList />
              </span>
              <span className="menu-title">{t("Devices")}</span>
            </Link>
          </Menu.Item>
          <Menu.Item key={ROUTES.GRAPH.key}>
            <Link to={ROUTES.GRAPH.path}>
              <span className="menu-icon">
                <FaChartLine />
              </span>
              <span className="menu-title">{t("Graph")}</span>
            </Link>
          </Menu.Item>
          <Menu.Item key={ROUTES.SCHEDULE.key}>
            <Link to={ROUTES.SCHEDULE.path}>
              <span className="menu-icon">
                <FaRegCalendarAlt />
              </span>
              <span className="menu-title">{t("Schedule")}</span>
            </Link>
          </Menu.Item>
          <Menu.Item key={ROUTES.CONTROL.key}>
            <Link to={ROUTES.CONTROL.path}>
              <span className="menu-icon">
                <FaToggleOn />
              </span>
              <span className="menu-title">{t("Control")}</span>
            </Link>
          </Menu.Item>
          <Menu.Item key={ROUTES.EVENTS.key}>
            <Link to={ROUTES.EVENTS.path}>
              <span className="menu-icon">
                <FaClipboardList />
              </span>
              <span className="menu-title">{t("Events")}</span>
            </Link>
          </Menu.Item>

          {(activeEntityId === GEC_ENTITY_ID || activeEntityId === GEC_TEST_ENTITY_ID) &&
            !userInfo?.username.endsWith(".edu") && (
              <SubMenu key="settings" icon={<FaCog />} title={t("SETTINGS")}>
                <Menu.Item key={ROUTES.RULE_SETTINGS.key}>
                  <Link to={ROUTES.RULE_SETTINGS.path}>
                    <span className="menu-title">{t("Rule Settings")}</span>
                  </Link>
                </Menu.Item>
                <Menu.Item key={ROUTES.BLOCK_IRRIGATION_SETTINGS.key}>
                  <Link to={ROUTES.BLOCK_IRRIGATION_SETTINGS.path}>
                    <span className="menu-title">{t("Block Irrigation")}</span>
                  </Link>
                </Menu.Item>
                <Menu.Item key={ROUTES.GLOBAL_SETTINGS.key}>
                  <Link to={ROUTES.GLOBAL_SETTINGS.path}>
                    <span className="menu-title">{t("Global Settings")}</span>
                  </Link>
                </Menu.Item>
              </SubMenu>
            )}
        </Menu>
      </> : <>
        <Menu
          className="nav-menu side-bar"
          theme="light"
          selectedKeys={[getMatchedKey(location)]}
          mode="inline"
          onClick={collapseIfMobile}
        >
          <Menu.Item key={ROUTES.RECOMMENDATION.key}>
            <Link to={ROUTES.RECOMMENDATION.path}>
              <span className="menu-icon">
                <FaHome />
              </span>
              <span className="menu-title">{t("Home")}</span>
            </Link>
          </Menu.Item>
          <Menu.Item key={ROUTES.ENTITY_MONITORING.key}>
            <Link to={ROUTES.ENTITY_MONITORING.path}>
              <span className="menu-icon"><FaClipboardList /></span>
              <span className="menu-title">{t("Entity Monitoring")}</span>
            </Link>
          </Menu.Item>
          <Menu.Item key={ROUTES.ADMIN_OPERATIONS.key}>
            <Link to={ROUTES.ADMIN_OPERATIONS.path}>
              <span className="menu-icon">
                <FaCog />
              </span>
              <span className="menu-title">{t("Status")}</span>
            </Link>
          </Menu.Item>
          <Menu.Item key={ROUTES.INSTALLATION_CHECK.key}>
            <Link to={ROUTES.INSTALLATION_CHECK.path}>
              <span className="menu-icon">
                <FaList />
              </span>
              <span className="menu-title">{t("Installation Check")}</span>
            </Link>
          </Menu.Item>
          <SubMenu
            key="update"
            icon={<FaClipboardList />}
            title="UPDATE"
            className="sub-menu-update-route"
            >
            <Menu.Item key={ROUTES.APPLY_CONFIG_PAGE.key}>
              <Link to={ROUTES.APPLY_CONFIG_PAGE.path}>
                <span className="menu-icon"><FaSyncAlt /></span>
                <span className="menu-title">{t("Refresh Config")}</span>
              </Link>
            </Menu.Item>
            <Menu.Item key={ROUTES.OTA_UPDATE.key}>
              <Link to={ROUTES.OTA_UPDATE.path}>
                <span className="menu-icon"><FaClipboardList /></span>
                <span className="menu-title">{t("Update Firmware")}</span>
              </Link>
            </Menu.Item>
            <Menu.Item key={ROUTES.DELETE_FIRMWARE_VERSION.key}>
              <Link to={ROUTES.DELETE_FIRMWARE_VERSION.path}>
                <span className="menu-icon"><FaClipboardList /></span>
                <span className="menu-title">{t("Delete Firmware Version")}</span>
              </Link>
            </Menu.Item>
          </SubMenu>
        </Menu>
      </>}
    </CollapsableSidebar>
  );
}

Sidebar.propTypes = {
  isMobile: PropTypes.bool,
  onCollapse: PropTypes.func,
  collapsed: PropTypes.bool,
};

Sidebar.defaultProps = {
  isMobile: false,
  onCollapse: undefined,
  collapsed: false,
};

export default withRouter(Sidebar);
